import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET, img, video } from './localAssets.js';
import {
	formatVideoDuration,
	normalizeLocalVideoItem,
	revokeLocalVideoItem,
} from './localVideo.js';

import { getMediaItemUrl, getQiniuSignedUrl } from './qiniuMedia.js';

export const VIDEO_ROW_TYPE = 'video-row';
export const VIDEO_ROW_DEFAULT_THUMB_HEIGHT = 72;
export const VIDEO_ROW_THUMB_ASPECT = 4 / 3;
export const VIDEO_ROW_DEFAULT_CARD_RADIUS = 12;
export const VIDEO_ROW_DEFAULT_COVER_RADIUS = 8;
export const VIDEO_ROW_CARD_PADDING = 12;
export const VIDEO_ROW_ITEM_GAP = 16;

const DEFAULT_TITLE_COLOR = '#333333';
const DEFAULT_DESC_COLOR = '#999999';
const DEFAULT_DURATION_COLOR = '#bbbbbb';

// 默认视频文件的字节数（占位值，待替换为 video-row-1/2/3.mp4 实际大小）
const VIDEO_ROW_DEFAULT_SIZE = {
	1: Math.round(2.20 * 1024 * 1024), 
	2: Math.round(1.96 * 1024 * 1024),
	3: Math.round(2.53 * 1024 * 1024), 
};
const VIDEO_ROW_DEFAULT_SIZE_LABEL = {
	1: '2.20 MB',
	2: '1.96 MB',
	3: '2.53 MB',
};

const ITEM_PRESETS = [
	{
		id: 'dv-32001',
		title: '芬兰｜坠入漫天飞雪的冬日梦境',
		description: '夜幕降临，雪花静静飘落。路灯晕开温柔光晕，大地被白雪温柔覆盖。',
		duration: 12,
		videoUrl: '/gedit/video/video-row-3.mp4',
	},
	{
		id: 'dv-32002',
		title: '上海｜一城灯火尽繁华',
		description: '黄昏落幕，华灯次第点亮。卢浦大桥舒展金色拱弧，远处东方明珠静静伫立。',
		duration: 15,
		videoUrl: '/gedit/video/video-row-2.mp4',
	},
	{
		id: 'dv-32003',
		title: '纽约｜奔赴曼哈顿的都市浪漫',
		description: '哈德逊河水缓缓流淌，自由女神静静伫立。摩天楼宇拔地而起，勾勒出纽约独有的城市轮廓。',
		duration: 11,
		videoUrl: '/gedit/video/video-row-1.mp4',
	},
];

function createDefaultCover(preset = {}, index = 0) {
	const filename = preset?.coverUrl || 'cover.jpg';
	const coverUrl = preset?.coverUrl
		? img(preset.coverUrl.replace('/gedit/images/', ''))
		: LOCAL_ASSET.videoRow[index % LOCAL_ASSET.videoRow.length];
	return {
		id: `video-row-cover-${uuidv4()}`,
		filename,
		previewUrl: coverUrl,
		url: '',
		local: false,
	};
}

function createDefaultVideo(preset = {}, index = 0) {
	const duration = preset.duration ?? 34;
	const filename = preset?.filename || '';
	const videoUrl = preset?.videoUrl
		? video(preset.videoUrl.replace('/gedit/video/', ''))
		: video('video-row-1.mp4');
	const presetIndex = index + 1;
	return {
		// 优先使用 preset 显式声明的 id（如 dv-20005），否则回退到 uuid（用户上传时）
		id: preset.id || `video-row-video-${uuidv4()}`,
		filename,
		filesize: VIDEO_ROW_DEFAULT_SIZE_LABEL[presetIndex] || '',
		filesize_byte: VIDEO_ROW_DEFAULT_SIZE[presetIndex] || 0,
		previewUrl: videoUrl,
		duration,
		durationText: formatVideoDuration(duration),
		url: videoUrl,
		local: false,
	};
}

export function createDefaultVideoRowItem(preset = {}, index = 0) {
	const duration = preset.duration ?? 34;
	return {
		id: preset.id || uuidv4(),
		title: preset.title || '视频标题',
		description: preset.description || '视频描述',
		titleColor: DEFAULT_TITLE_COLOR,
		descColor: DEFAULT_DESC_COLOR,
		durationColor: DEFAULT_DURATION_COLOR,
		cover: createDefaultCover(preset, index),
		video: createDefaultVideo(preset, index),
	};
}

export function createDefaultVideoRowItems(count = 3) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		items.push(createDefaultVideoRowItem(ITEM_PRESETS[i] || {}, i));
	}
	return items;
}

export function getVideoRowDefaults() {
	return normalizeVideoRowData({
		items: createDefaultVideoRowItems(3),
		showCard: false,
	});
}

export function isVideoRowBlock(blockType) {
	return blockType === VIDEO_ROW_TYPE;
}

export function getVideoRowCoverUrl(item) {
	if (!item?.cover) return '';
	const cover = item.cover;
	if (cover.previewUrl || cover.url) {
		return getMediaItemUrl(cover);
	}
	if (item?.video) {
		const rawUrl = item.video.url || item.video.previewUrl || '';
		if (rawUrl) {
			return getQiniuSignedUrl(rawUrl, 0);
		}
	}
	return '';
}

export function getVideoRowVideoUrl(item) {
	if (!item?.video) return '';
	return getMediaItemUrl(item.video);
}

function normalizeCoverImage(image, fallback) {
	if (!image) {
		return { id: '', filename: '', previewUrl: '', url: '', local: false };
	}
	if (!image.previewUrl && !image.url) {
		return { id: '', filename: '', previewUrl: '', url: '', local: false };
	}
	return normalizeStoredImageItem(image, fallback);
}

function normalizeVideoAsset(video, fallback) {
	if (!video?.previewUrl && !video?.url && !video?.duration) {
		return fallback ? { ...fallback } : createDefaultVideo();
	}
	return normalizeLocalVideoItem({
		...fallback,
		...video,
	});
}

export function normalizeVideoRowItem(item = {}, index = 0) {
	const fallback = createDefaultVideoRowItem(ITEM_PRESETS[index] || {}, index);
	return {
		id: item.id || uuidv4(),
		title: item.title ?? fallback.title,
		description: item.description ?? fallback.description,
		titleColor: normalizeHexColor(item.titleColor || fallback.titleColor),
		descColor: normalizeHexColor(item.descColor || fallback.descColor),
		durationColor: normalizeHexColor(item.durationColor || fallback.durationColor),
		cover: normalizeCoverImage(item.cover, fallback.cover),
		video: normalizeVideoAsset(item.video, fallback.video),
	};
}

export function normalizeVideoRowItems(items = []) {
	if (!items.length) {
		return createDefaultVideoRowItems(3);
	}
	return items.map((item, index) => normalizeVideoRowItem(item, index));
}

export function normalizeVideoRowData(data = {}) {
	const thumbHeight = Number(data.thumbHeight);
	return {
		items: normalizeVideoRowItems(data.items),
		showCard: data.showCard === true,
		cardBorderRadius: Number.isFinite(Number(data.cardBorderRadius))
			? Math.max(0, Number(data.cardBorderRadius))
			: VIDEO_ROW_DEFAULT_CARD_RADIUS,
		coverBorderRadius: Number.isFinite(Number(data.coverBorderRadius))
			? Math.max(0, Number(data.coverBorderRadius))
			: VIDEO_ROW_DEFAULT_COVER_RADIUS,
		thumbHeight: Number.isFinite(thumbHeight) && thumbHeight > 0
			? thumbHeight
			: VIDEO_ROW_DEFAULT_THUMB_HEIGHT,
	};
}

export function getVideoRowThumbWidth(thumbHeight = VIDEO_ROW_DEFAULT_THUMB_HEIGHT) {
	return Math.round((Number(thumbHeight) || VIDEO_ROW_DEFAULT_THUMB_HEIGHT) * VIDEO_ROW_THUMB_ASPECT);
}

export function getVideoRowBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizeVideoRowData(data);
	const pxRatio = h5 ? 50 : 1;
	const coverRadius = normalized.coverBorderRadius;
	const cardRadius = normalized.cardBorderRadius;
	const thumbHeight = normalized.thumbHeight;
	const thumbWidth = getVideoRowThumbWidth(thumbHeight);

	return {
		thumbWidth: h5 ? `${thumbWidth / pxRatio}rem` : `${thumbWidth}px`,
		thumbHeight: h5 ? `${thumbHeight / pxRatio}rem` : `${thumbHeight}px`,
		coverBorderRadius: h5 ? `${coverRadius / pxRatio}rem` : `${coverRadius}px`,
		cardBorderRadius: h5 ? `${cardRadius / pxRatio}rem` : `${cardRadius}px`,
		cardPadding: h5 ? `${VIDEO_ROW_CARD_PADDING / pxRatio}rem` : `${VIDEO_ROW_CARD_PADDING}px`,
		itemGap: h5 ? `${VIDEO_ROW_ITEM_GAP / pxRatio}rem` : `${VIDEO_ROW_ITEM_GAP}px`,
		showCard: normalized.showCard,
	};
}

export function replaceVideoRowCover(currentCover, file, createLocalImageItem) {
	if (currentCover?.local) {
		revokeLocalImageUrl(currentCover.previewUrl);
	}
	return createLocalImageItem(file);
}

export function replaceVideoRowVideo(currentVideo, nextVideo) {
	if (currentVideo?.local) {
		revokeLocalVideoItem(currentVideo);
	}
	return normalizeLocalVideoItem(nextVideo);
}

export function revokeVideoRowItems(items = []) {
	items.forEach((item) => {
		if (item?.cover?.local) {
			revokeLocalImageUrl(item.cover.previewUrl);
		}
		if (item?.video?.local) {
			revokeLocalVideoItem(item.video);
		}
	});
}
